// Parser universel données brutes TURPE
// Détecte automatiquement le format (ENEDIS ou OUTIL) et extrait le numéro de PDL

export const parserDonneesBrutesTURPE = (csvContent) => {
  const lignes = csvContent.split('\n').filter(l => l.trim());
  
  if (lignes.length === 0) {
    throw new Error('Fichier vide');
  }
  
  // Détecter format
  const premiereLigne = lignes[0];
  const colonnes = premiereLigne.split(';');
  
  let format;
  if (colonnes.length > 10) {
    format = 'ENEDIS';
  } else if (colonnes.length >= 2 && colonnes.length <= 4) {
    format = 'OUTIL';
  } else {
    throw new Error(`Format inconnu (${colonnes.length} colonnes)`);
  }
  
  console.log(`Format détecté : ${format}`);
  
  if (format === 'ENEDIS') {
    return parserENEDIS(lignes);
  } else {
    return parserOutil(lignes);
  }
};

/**
 * Extrait le numéro de PDL d'un fichier CSV
 * Cherche dans les en-têtes et les premières lignes de données
 */
export const extractPDLFromCSV = (csvContent) => {
  const lignes = csvContent.split('\n').filter(l => l.trim());
  
  if (lignes.length === 0) return null;
  
  // Pattern PDL : 14 chiffres (les PRM ENEDIS peuvent commencer par n'importe quel chiffre 1-9 selon la région)
  const pdlPattern = /\b(\d{14})\b/;
  
  // Chercher dans les 10 premières lignes
  for (let i = 0; i < Math.min(10, lignes.length); i++) {
    const ligne = lignes[i];
    const colonnes = ligne.split(';');
    
    // Format ENEDIS : le PDL est souvent dans la colonne 1 (index 0)
    if (colonnes.length > 10) {
      // Colonne 1 contient souvent le PDL
      const col1 = colonnes[0]?.replace(/"/g, '').trim();
      if (col1 && pdlPattern.test(col1)) {
        const match = col1.match(pdlPattern);
        if (match) {
          console.log(`PDL trouvé (ENEDIS col 1): ${match[1]}`);
          return match[1];
        }
      }
      
      // Parfois le PDL est dans la colonne 2
      const col2 = colonnes[1]?.replace(/"/g, '').trim();
      if (col2 && pdlPattern.test(col2)) {
        const match = col2.match(pdlPattern);
        if (match) {
          console.log(`PDL trouvé (ENEDIS col 2): ${match[1]}`);
          return match[1];
        }
      }
    }
    
    // Chercher le PDL n'importe où dans la ligne
    const match = ligne.match(pdlPattern);
    if (match) {
      console.log(`PDL trouvé (pattern général): ${match[1]}`);
      return match[1];
    }
  }
  
  // Essayer de trouver dans le nom du fichier (sera passé séparément)
  return null;
};

/**
 * Extrait le PDL du nom de fichier
 * Ex: "30001234567890_2024.csv" → "30001234567890"
 */
export const extractPDLFromFilename = (filename) => {
  if (!filename) return null;
  
  const pdlPattern = /(\d{14})/;
  const match = filename.match(pdlPattern);
  
  if (match) {
    console.log(`PDL trouvé dans le nom de fichier: ${match[1]}`);
    return match[1];
  }
  
  return null;
};

const parserENEDIS = (lignes) => {
  // Format ENEDIS:
  // Col 0: Identifiant PRM (PDL)
  // Col 3: Grandeur physique (PA = Puissance Active, PRI = Puissance Réactive Inductive)
  // Col 6: Unité (W ou VAr)
  // Col 7: Horodate
  // Col 8: Valeur
  
  let pdl = null;
  const donneesPuissance = {};
  
  for (let i = 1; i < lignes.length; i++) {
    const cols = lignes[i].split(';');
    
    if (cols.length < 9) continue;
    
    // Extraire le PDL de la colonne 0 (PRM ENEDIS = 14 chiffres, premier digit libre)
    if (!pdl) {
      const col0 = cols[0]?.replace(/"/g, '').trim();
      const pdlPattern = /^\d{14}$/;
      if (pdlPattern.test(col0)) {
        pdl = col0;
      }
    }
    
    // Colonne 3 = Grandeur physique (PA ou PRI)
    const grandeur = cols[3]?.replace(/"/g, '').trim().toUpperCase();
    // Colonne 6 = Unité (W ou VAr)
    const unite = cols[6]?.replace(/"/g, '').trim().toLowerCase();
    // Colonne 7 = Horodatage
    const horodate = cols[7]?.replace(/"/g, '').trim();
    // Colonne 8 = Valeur
    const valeur = parseFloat(cols[8]?.replace(/"/g, '').replace(',', '.'));
    
    if (!horodate || isNaN(valeur)) continue;
    
    if (!donneesPuissance[horodate]) {
      donneesPuissance[horodate] = { PA: null, PR: null, PRI: null, PRC: null };
    }
    
    // Convertir W → kW ou VAr → kVAr
    const kW_ou_kVAr = valeur / 1000;
    
    // Identifier PA (Puissance Active) ou PRI/PRC (Réactive Inductive/Capacitive)
    // PA avec unité W = Puissance Active
    // PRI avec unité VAr = Puissance Réactive Inductive (absorbée)
    // PRC avec unité VAr = Puissance Réactive Capacitive (injectée)
    if (grandeur === 'PA' || unite === 'w') {
      donneesPuissance[horodate].PA = kW_ou_kVAr;
    } else if (grandeur === 'PRI') {
      donneesPuissance[horodate].PRI = kW_ou_kVAr;
      // Compat : on stocke aussi en PR pour les consommateurs existants
      if (donneesPuissance[horodate].PR === null) donneesPuissance[horodate].PR = kW_ou_kVAr;
    } else if (grandeur === 'PRC') {
      donneesPuissance[horodate].PRC = kW_ou_kVAr;
    } else if (grandeur === 'PR' || unite === 'var') {
      donneesPuissance[horodate].PR = kW_ou_kVAr;
    }
  }
  
  // Calculer kVA et garder kW/kVAr pour chaque horodate
  const tousLesPoints = [];
  let hasReactiveData = false; // true si au moins une donnée réactive existe (même si = 0)
  let hasNonZeroReactive = false; // true si au moins une donnée réactive > 0
  let hasInductiveCapacitive = false; // true si colonnes PRI/PRC présentes (legacy)
  let hasInductiveData = false; // true si au moins un PRI > 0
  let hasCapacitiveData = false; // true si au moins un PRC > 0
  
  for (const horodate in donneesPuissance) {
    const PA_kW = donneesPuissance[horodate].PA || 0;
    const PR_kVAr = donneesPuissance[horodate].PR;
    const PRI_kVAr = donneesPuissance[horodate].PRI;
    const PRC_kVAr = donneesPuissance[horodate].PRC;
    
    // Vérifier si des données réactives existent (PR !== null)
    if (PR_kVAr !== null || PRI_kVAr !== null || PRC_kVAr !== null) {
      hasReactiveData = true;
      if ((PR_kVAr && PR_kVAr > 0) || (PRI_kVAr && PRI_kVAr > 0) || (PRC_kVAr && PRC_kVAr > 0)) {
        hasNonZeroReactive = true;
      }
    }
    if (PRI_kVAr !== null || PRC_kVAr !== null) {
      hasInductiveCapacitive = true;
    }
    if (PRI_kVAr && PRI_kVAr > 0) hasInductiveData = true;
    if (PRC_kVAr && PRC_kVAr > 0) hasCapacitiveData = true;
    
    // kVA = √(kW² + kVAr²)
    // Si PRI/PRC séparées : on prend la résultante Q_net = PRI - PRC
    const PR_value = (PRI_kVAr !== null || PRC_kVAr !== null)
      ? ((PRI_kVAr || 0) - (PRC_kVAr || 0))
      : (PR_kVAr !== null ? PR_kVAr : 0);
    const kVA = Math.sqrt(PA_kW * PA_kW + PR_value * PR_value);
    
    tousLesPoints.push({
      horodate: horodate,
      kW: PA_kW,
      kVA: hasReactiveData ? kVA : null, // null si pas de données réactives
      kVAr: PR_value,
      kVAr_inductif: PRI_kVAr !== null ? PRI_kVAr : null,
      kVAr_capacitif: PRC_kVAr !== null ? PRC_kVAr : null
    });
  }
  
  // Détecter le pas de temps (en minutes)
  const pasTempsMinutes = detecterPasTemps(tousLesPoints);
  
  console.log(`✓ ${tousLesPoints.length} points de mesure avec kW et kVA`);
  console.log(`✓ Données réactives présentes: ${hasReactiveData} (non-nulles: ${hasNonZeroReactive})`);
  console.log(`✓ Pas de temps détecté: ${pasTempsMinutes} minutes`);
  if (pdl) console.log(`✓ PDL détecté: ${pdl}`);
  
  return {
    pdl: pdl,
    tousLesPoints: tousLesPoints,
    puissancesMaxParMois: calculerPuissancesMaxParMois(tousLesPoints, hasReactiveData),
    hasReactiveData: hasReactiveData,
    hasInductiveCapacitive: hasInductiveCapacitive,
    hasInductiveData: hasInductiveData,
    hasCapacitiveData: hasCapacitiveData,
    pasTempsMinutes: pasTempsMinutes
  };
};

const parserOutil = (lignes) => {
  const tousLesPoints = [];
  let pdl = null;
  let hasReactiveData = false;
  
  // Chercher le PDL dans l'en-tête ou les premières lignes
  for (let i = 0; i < Math.min(5, lignes.length); i++) {
    const pdlMatch = lignes[i].match(/(\d{14})/);
    if (pdlMatch) {
      pdl = pdlMatch[1];
      break;
    }
  }
  
  for (let i = 1; i < lignes.length; i++) {
    const cols = lignes[i].split(';');
    
    if (cols.length < 2) continue;
    
    const date = cols[0]?.replace(/"/g, '').trim();
    const kW = parseFloat(cols[1]);
    
    if (!date || isNaN(kW)) continue;
    
    // Si 3 colonnes, la 3ème peut être kVA
    let kVA = null;
    if (cols.length >= 3) {
      const kVA_val = parseFloat(cols[2]);
      if (!isNaN(kVA_val)) {
        kVA = kVA_val;
        hasReactiveData = true; // On considère qu'il y a des données réactives si colonne kVA présente
      }
    }
    
    tousLesPoints.push({
      horodate: date,
      kW: kW,
      kVA: kVA
    });
  }
  
  // Détecter le pas de temps (en minutes)
  const pasTempsMinutes = detecterPasTemps(tousLesPoints);
  
  console.log(`✓ ${tousLesPoints.length} points de mesure`);
  console.log(`✓ Données kVA présentes: ${hasReactiveData}`);
  console.log(`✓ Pas de temps détecté: ${pasTempsMinutes} minutes`);
  if (pdl) console.log(`✓ PDL détecté: ${pdl}`);
  
  return {
    pdl: pdl,
    tousLesPoints: tousLesPoints,
    puissancesMaxParMois: calculerPuissancesMaxParMois(tousLesPoints, hasReactiveData),
    hasReactiveData: hasReactiveData,
    pasTempsMinutes: pasTempsMinutes
  };
};

/**
 * Détecte le pas de temps en minutes entre les points de mesure
 * Analyse les premiers points pour déterminer l'intervalle (5min, 10min, 15min, 30min, 60min)
 */
export const detecterPasTemps = (tousLesPoints) => {
  if (tousLesPoints.length < 2) return 10; // Défaut: 10 minutes
  
  // Trier les points par horodate
  const pointsTries = [...tousLesPoints].sort((a, b) => 
    new Date(a.horodate) - new Date(b.horodate)
  );
  
  // Calculer les différences entre les 10 premiers points consécutifs
  const differences = [];
  for (let i = 1; i < Math.min(20, pointsTries.length); i++) {
    const date1 = new Date(pointsTries[i - 1].horodate);
    const date2 = new Date(pointsTries[i].horodate);
    const diffMinutes = (date2 - date1) / (1000 * 60);
    
    if (diffMinutes > 0 && diffMinutes <= 60) {
      differences.push(diffMinutes);
    }
  }
  
  if (differences.length === 0) return 10; // Défaut
  
  // Trouver le mode (valeur la plus fréquente)
  const counts = {};
  differences.forEach(d => {
    const rounded = Math.round(d);
    counts[rounded] = (counts[rounded] || 0) + 1;
  });
  
  let mode = 10;
  let maxCount = 0;
  for (const [value, count] of Object.entries(counts)) {
    if (count > maxCount) {
      maxCount = count;
      mode = parseInt(value);
    }
  }
  
  // Valider que c'est un pas de temps standard (5, 10, 15, 30, 60)
  const pasStandards = [5, 10, 15, 30, 60];
  const pasPlusProche = pasStandards.reduce((prev, curr) => 
    Math.abs(curr - mode) < Math.abs(prev - mode) ? curr : prev
  );
  
  return pasPlusProche;
};

export const calculerPuissancesMaxParMois = (tousLesPoints, hasReactiveData = true) => {
  const pmaxParMois = {};
  const pmaxKWParMois = {};
  const nbPointsParMois = {};
  
  tousLesPoints.forEach(point => {
    const mois = point.horodate.substring(0, 7);
    
    if (!pmaxParMois[mois]) {
      pmaxParMois[mois] = 0;
      pmaxKWParMois[mois] = 0;
      nbPointsParMois[mois] = 0;
    }
    
    // Utiliser kVA si disponible, sinon kW
    const valeurPmax = hasReactiveData && point.kVA !== null ? point.kVA : point.kW;
    pmaxParMois[mois] = Math.max(pmaxParMois[mois], valeurPmax || 0);
    pmaxKWParMois[mois] = Math.max(pmaxKWParMois[mois], point.kW || 0);
    nbPointsParMois[mois]++;
  });
  
  const resultat = [];
  
  for (const mois in pmaxParMois) {
    const nbPoints = nbPointsParMois[mois];
    const [annee, moisNum] = mois.split('-').map(Number);
    const joursParMois = new Date(annee, moisNum, 0).getDate();
    const attendu = joursParMois * 288;
    const tauxComplet = (nbPoints / attendu * 100);
    
    resultat.push({
      mois: mois,
      pmax_kVA: Math.round(pmaxParMois[mois] * 10) / 10,
      pmax_kW: Math.round(pmaxKWParMois[mois] * 10) / 10,
      nb_points: nbPoints,
      taux_complet: tauxComplet,
      qualite: tauxComplet > 90 ? 'Bon' : tauxComplet > 70 ? 'Moyen' : 'Faible'
    });
  }
  
  return resultat;
};

/**
 * Détecte si un fichier CSV contient plusieurs PDL
 * @returns {string[]} Liste des PDL trouvés (ou vide si mono ou non-ENEDIS)
 */
export const detecterPDLsMultiples = (csvContent) => {
  const lignes = csvContent.split('\n');
  const premiereLigne = lignes[0];
  const colonnes = premiereLigne.split(';');
  
  // Seulement pour le format ENEDIS (>10 colonnes)
  if (colonnes.length <= 10) return [];
  
  const pdlSet = new Set();
  const pdlPattern = /^\d{14}$/;
  
  for (let i = 1; i < lignes.length; i++) {
    const col0 = lignes[i].split(';')[0]?.replace(/"/g, '').trim();
    if (col0 && pdlPattern.test(col0)) {
      pdlSet.add(col0);
      if (pdlSet.size > 1) return [...pdlSet]; // Arrêt rapide : multi-PDL détecté suffisamment tôt
    }
    // Scan partiel pour performance (vérifier tous les ~50000 lignes)
    if (i > 1 && pdlSet.size === 1 && i % 50000 === 0) {
      // Continue scanning - les PDL sont séquentiels
    }
  }
  
  return [...pdlSet];
};

/**
 * Parser ENEDIS multi-PDL : sépare un fichier unique en données par PDL
 * Gère les données séquentielles (toutes les lignes d'un PDL puis le suivant)
 * Détecte PA (W) et PRI (VAr) pour chaque PDL
 * 
 * @returns {Object} { pdl1: {tousLesPoints, puissancesMaxParMois, hasReactiveData, pasTempsMinutes}, pdl2: {...}, ... }
 */
export const parserENEDISMultiPDL = (csvContent, onProgress) => {
  const lignes = csvContent.split('\n').filter(l => l.trim());
  
  if (lignes.length < 2) throw new Error('Fichier vide');
  
  // Regrouper par PDL
  const donneesBrutesByPDL = {};
  const totalLignes = lignes.length - 1;
  
  for (let i = 1; i < lignes.length; i++) {
    const cols = lignes[i].split(';');
    if (cols.length < 9) continue;
    
    const pdl = cols[0]?.replace(/"/g, '').trim();
    if (!pdl || !/^\d{14}$/.test(pdl)) continue;
    
    if (!donneesBrutesByPDL[pdl]) {
      donneesBrutesByPDL[pdl] = {};
    }
    
    const grandeur = cols[3]?.replace(/"/g, '').trim().toUpperCase();
    const unite = cols[6]?.replace(/"/g, '').trim().toLowerCase();
    const horodate = cols[7]?.replace(/"/g, '').trim();
    const valeur = parseFloat(cols[8]?.replace(/"/g, '').replace(',', '.'));
    
    if (!horodate || isNaN(valeur)) continue;
    
    if (!donneesBrutesByPDL[pdl][horodate]) {
      donneesBrutesByPDL[pdl][horodate] = { PA: null, PR: null, PRI: null, PRC: null };
    }
    
    const kW_ou_kVAr = valeur / 1000; // W → kW ou VAr → kVAr
    
    if (grandeur === 'PA' || unite === 'w') {
      donneesBrutesByPDL[pdl][horodate].PA = kW_ou_kVAr;
    } else if (grandeur === 'PRI') {
      donneesBrutesByPDL[pdl][horodate].PRI = kW_ou_kVAr;
      if (donneesBrutesByPDL[pdl][horodate].PR === null) donneesBrutesByPDL[pdl][horodate].PR = kW_ou_kVAr;
    } else if (grandeur === 'PRC') {
      donneesBrutesByPDL[pdl][horodate].PRC = kW_ou_kVAr;
    } else if (grandeur === 'PR' || unite === 'var') {
      donneesBrutesByPDL[pdl][horodate].PR = kW_ou_kVAr;
    }
    
    // Callback de progression (tous les 50000 lignes)
    if (onProgress && i % 50000 === 0) {
      onProgress(Math.round((i / totalLignes) * 100));
    }
  }
  
  // Construire les résultats par PDL (finalisation réutilisable)
  const resultats = {};
  for (const pdl of Object.keys(donneesBrutesByPDL)) {
    resultats[pdl] = finaliserDonneesPDL(pdl, donneesBrutesByPDL[pdl]);
  }
  
  console.log(`✓ Multi-PDL: ${Object.keys(resultats).length} PDL extraits : ${Object.keys(resultats).join(', ')}`);
  
  return resultats;
};

/**
 * Accumule une ligne ENEDIS dans la map { [pdl]: { [horodate]: {PA,PR,PRI,PRC} } }.
 * Utilisé par le parser en mémoire ET par le parser en streaming (Web Worker).
 * @returns {string|null} le numéro de PDL de la ligne (ou null si ligne ignorée)
 */
export const accumulerLigneENEDIS = (line, accByPDL) => {
  const cols = line.split(';');
  if (cols.length < 9) return null;
  const pdl = cols[0]?.replace(/"/g, '').trim();
  if (!pdl || !/^\d{14}$/.test(pdl)) return null;
  const grandeur = cols[3]?.replace(/"/g, '').trim().toUpperCase();
  const unite = cols[6]?.replace(/"/g, '').trim().toLowerCase();
  const horodate = cols[7]?.replace(/"/g, '').trim();
  const valeur = parseFloat(cols[8]?.replace(/"/g, '').replace(',', '.'));
  if (!horodate || isNaN(valeur)) return pdl;
  if (!accByPDL[pdl]) accByPDL[pdl] = {};
  if (!accByPDL[pdl][horodate]) accByPDL[pdl][horodate] = { PA: null, PR: null, PRI: null, PRC: null };
  const kW_ou_kVAr = valeur / 1000;
  if (grandeur === 'PA' || unite === 'w') {
    accByPDL[pdl][horodate].PA = kW_ou_kVAr;
  } else if (grandeur === 'PRI') {
    accByPDL[pdl][horodate].PRI = kW_ou_kVAr;
    if (accByPDL[pdl][horodate].PR === null) accByPDL[pdl][horodate].PR = kW_ou_kVAr;
  } else if (grandeur === 'PRC') {
    accByPDL[pdl][horodate].PRC = kW_ou_kVAr;
  } else if (grandeur === 'PR' || unite === 'var') {
    accByPDL[pdl][horodate].PR = kW_ou_kVAr;
  }
  return pdl;
};

/**
 * Transforme la map { [horodate]: {PA,PR,PRI,PRC} } d'un PDL en résultat final
 * (tousLesPoints + agrégats). Logique IDENTIQUE à parserENEDISMultiPDL (ne pas régresser).
 */
export const finaliserDonneesPDL = (pdl, donnees) => {
  const tousLesPoints = [];
  let hasReactiveData = false;
  let hasInductiveCapacitive = false;
  let hasInductiveData = false;
  let hasCapacitiveData = false;
  
  for (const horodate in donnees) {
    const PA_kW = donnees[horodate].PA || 0;
    const PR_kVAr = donnees[horodate].PR;
    const PRI_kVAr = donnees[horodate].PRI;
    const PRC_kVAr = donnees[horodate].PRC;
    
    if (PR_kVAr !== null || PRI_kVAr !== null || PRC_kVAr !== null) {
      hasReactiveData = true;
    }
    if (PRI_kVAr !== null || PRC_kVAr !== null) hasInductiveCapacitive = true;
    if (PRI_kVAr && PRI_kVAr > 0) hasInductiveData = true;
    if (PRC_kVAr && PRC_kVAr > 0) hasCapacitiveData = true;
    
    const PR_value = (PRI_kVAr !== null || PRC_kVAr !== null)
      ? ((PRI_kVAr || 0) - (PRC_kVAr || 0))
      : (PR_kVAr !== null ? PR_kVAr : 0);
    const kVA = Math.sqrt(PA_kW * PA_kW + PR_value * PR_value);
    
    tousLesPoints.push({
      horodate,
      kW: PA_kW,
      kVA: hasReactiveData ? kVA : null,
      kVAr: PR_value,
      kVAr_inductif: PRI_kVAr,
      kVAr_capacitif: PRC_kVAr
    });
  }
  
  const pasTempsMinutes = detecterPasTemps(tousLesPoints);
  
  return {
    pdl,
    tousLesPoints,
    puissancesMaxParMois: calculerPuissancesMaxParMois(tousLesPoints, hasReactiveData),
    hasReactiveData,
    hasInductiveCapacitive,
    hasInductiveData,
    hasCapacitiveData,
    pasTempsMinutes
  };
};

/**
 * Fusionne deux résultats d'un même PDL (cas rare : PDL non contigu dans le fichier).
 */
export const fusionnerResultatsPDL = (a, b) => {
  const tousLesPoints = [...(a.tousLesPoints || []), ...(b.tousLesPoints || [])];
  const hasReactiveData = a.hasReactiveData || b.hasReactiveData;
  return {
    pdl: a.pdl,
    tousLesPoints,
    puissancesMaxParMois: calculerPuissancesMaxParMois(tousLesPoints, hasReactiveData),
    hasReactiveData,
    hasInductiveCapacitive: a.hasInductiveCapacitive || b.hasInductiveCapacitive,
    hasInductiveData: a.hasInductiveData || b.hasInductiveData,
    hasCapacitiveData: a.hasCapacitiveData || b.hasCapacitiveData,
    pasTempsMinutes: a.pasTempsMinutes || b.pasTempsMinutes
  };
};

// Export
export default parserDonneesBrutesTURPE;
